import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { config } from "./config";

// Check if credentials are configured
const hasValidConfig =
  config.cognito.userPoolId &&
  config.cognito.userPoolClientId &&
  !config.cognito.userPoolId.includes("placeholder") &&
  !config.cognito.userPoolClientId.includes("placeholder");

const poolData = hasValidConfig
  ? {
      UserPoolId: config.cognito.userPoolId,
      ClientId: config.cognito.userPoolClientId,
    }
  : null;

const userPool = poolData ? new CognitoUserPool(poolData) : null;

export interface AuthUser {
  email: string;
  sub: string;
}

export interface NewPasswordRequiredResult {
  type: "NEW_PASSWORD_REQUIRED";
  cognitoUser: CognitoUser;
}

export interface MfaSetupRequiredResult {
  type: "MFA_SETUP_REQUIRED";
  cognitoUser: CognitoUser;
  secretCode: string;
}

export interface MfaTotpRequiredResult {
  type: "MFA_TOTP_REQUIRED";
  cognitoUser: CognitoUser;
}

export type SignInResult =
  | { type: "SUCCESS"; session: CognitoUserSession }
  | NewPasswordRequiredResult
  | MfaSetupRequiredResult
  | MfaTotpRequiredResult;

export async function signIn(
  email: string,
  password: string,
): Promise<SignInResult> {
  if (!userPool) {
    throw new Error(
      "Cognito is not configured. Please deploy AdminStack and configure .env",
    );
  }

  const authenticationData = {
    Username: email,
    Password: password,
  };

  const authenticationDetails = new AuthenticationDetails(authenticationData);

  const userData = {
    Username: email,
    Pool: userPool,
  };

  const cognitoUser = new CognitoUser(userData);

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        storeTokens(session);
        resolve({ type: "SUCCESS", session });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: () => {
        resolve({ type: "NEW_PASSWORD_REQUIRED", cognitoUser });
      },
      mfaSetup: () => {
        // User needs to set up TOTP MFA
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode: string) => {
            resolve({ type: "MFA_SETUP_REQUIRED", cognitoUser, secretCode });
          },
          onFailure: (err) => {
            reject(err);
          },
        });
      },
      totpRequired: () => {
        resolve({ type: "MFA_TOTP_REQUIRED", cognitoUser });
      },
    });
  });
}

function storeTokens(session: CognitoUserSession): void {
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();

  localStorage.setItem("access_token", accessToken);
  localStorage.setItem("id_token", idToken);
  localStorage.setItem("refresh_token", refreshToken);
  localStorage.setItem(
    "expiry",
    ((session.getAccessToken().getExpiration() - 60) * 1000).toString(),
  );
}

export async function verifyTotpSetup(
  cognitoUser: CognitoUser,
  totpCode: string,
  friendlyDeviceName: string = "Authenticator App",
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(totpCode, friendlyDeviceName, {
      onSuccess: (session) => {
        storeTokens(session);
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export async function respondToTotpChallenge(
  cognitoUser: CognitoUser,
  totpCode: string,
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      totpCode,
      {
        onSuccess: (session) => {
          storeTokens(session);
          resolve(session);
        },
        onFailure: (err) => {
          reject(err);
        },
      },
      "SOFTWARE_TOKEN_MFA",
    );
  });
}

export type NewPasswordResult =
  | { type: "SUCCESS"; session: CognitoUserSession }
  | MfaSetupRequiredResult;

export async function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
): Promise<NewPasswordResult> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => {
          storeTokens(session);
          resolve({ type: "SUCCESS", session });
        },
        onFailure: (err) => {
          reject(err);
        },
        mfaSetup: () => {
          // User needs to set up TOTP MFA after password change
          cognitoUser.associateSoftwareToken({
            associateSecretCode: (secretCode: string) => {
              resolve({ type: "MFA_SETUP_REQUIRED", cognitoUser, secretCode });
            },
            onFailure: (err) => {
              reject(err);
            },
          });
        },
      },
    );
  });
}

export async function signOut(): Promise<void> {
  if (!userPool) return;

  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }

  // Clear local storage
  localStorage.removeItem("access_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expiry");
}

export async function refreshTokens(): Promise<void> {
  if (!userPool) {
    throw new Error("Cognito is not configured");
  }

  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) throw new Error("No refresh token found");

  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) throw new Error("No current user");

  return new Promise((resolve, reject) => {
    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session) {
          reject(new Error("No session"));
          return;
        }

        const accessToken = session.getAccessToken().getJwtToken();
        const idToken = session.getIdToken().getJwtToken();

        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("id_token", idToken);
        localStorage.setItem(
          "expiry",
          (
            new Date().getTime() +
            session.getAccessToken().getExpiration() * 1000
          ).toString(),
        );

        resolve();
      },
    );
  });
}

export async function checkAuthenticated(): Promise<boolean> {
  const idToken = localStorage.getItem("id_token");
  const expiry = localStorage.getItem("expiry");

  if (!idToken) {
    return false;
  }

  // Check if token is expired
  if (expiry && new Date().getTime() > parseInt(expiry)) {
    try {
      await refreshTokens();
      return true;
    } catch (e) {
      console.error("Failed to refresh tokens", e);
      return false;
    }
  }

  return true;
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!userPool) return null;

  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return null;

  return new Promise((resolve, reject) => {
    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session) {
          resolve(null);
          return;
        }

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
            return;
          }

          const email = attributes?.find(
            (attr) => attr.Name === "email",
          )?.Value;
          const sub = attributes?.find((attr) => attr.Name === "sub")?.Value;

          if (!email || !sub) {
            resolve(null);
            return;
          }

          resolve({ email, sub });
        });
      },
    );
  });
}
